import type { BadgeVariant } from '@/components/common/Badge';

// Task status and priority types
export type TaskStatus =
  | 'open'
  | 'assigned'
  | 'in_progress'
  | 'pending_review'
  | 'complete'
  | 'cancelled'
  | 'on_hold';

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export type SlaStatus = 'ok' | 'warning' | 'critical' | 'overdue';

// Task type field schema types
export interface TaskTypeFieldConditional {
  field: string;
  value: unknown;
}

export interface TaskTypeField {
  name: string;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'phone'
    | 'email'
    | 'address'
    | 'select'
    | 'multiselect'
    | 'boolean'
    | 'date'
    | 'datetime';
  label: string;
  required?: boolean;
  options?: string[];
  conditional?: TaskTypeFieldConditional;
}

export interface TaskTypeFieldSchema {
  fields: TaskTypeField[];
}

// Task Type entity
export interface TaskType {
  id: string;
  tenant_id: string | null;
  code: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  field_schema: TaskTypeFieldSchema;
  // Title template for auto-generating task titles from custom fields
  // Uses {field_name} placeholders, e.g. "Welfare Check - {person_names} at {address}"
  title_template: string | null;
  default_sla_warning: number | null;
  default_sla_critical: number | null;
  escalation_enabled: boolean;
  escalation_after_minutes: number | null;
  escalation_to_role: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// Location coordinates
export interface LocationCoordinates {
  lat: number;
  lng: number;
}

// User reference for joins
export interface UserReference {
  id: string;
  name: string;
  email: string;
}

// Incident reference for joins
export interface IncidentReference {
  id: string;
  name: string;
  incident_number: string;
}

// Parent/child task reference for joins
export interface TaskReference {
  id: string;
  task_number: string;
  title: string;
  status: TaskStatus;
  location_name?: string | null;
}

// ESF reference for task assignment
export interface ESFReference {
  id: string;
  number: number;
  name: string;
  color: string;
}

// Task entity
export interface Task {
  id: string;
  tenant_id: string;
  incident_id: string | null;
  task_number: string;
  task_type_id: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  source_module: string | null;
  source_id: string | null;
  // Parent task for task hierarchies (spawned tasks)
  parent_task_id: string | null;
  // Protocol activation that created this task
  protocol_instance_id: string | null;
  assigned_to: string | null;
  assigned_at: string | null;
  assigned_by: string | null;
  // ESF assignment (can coexist with user assignment)
  assigned_esf_id: string | null;
  // Unified org tree assignment (incident_org_nodes)
  org_node_id: string | null;
  location_name: string | null;
  location_address: string | null;
  location_coordinates: LocationCoordinates | null;
  due_at: string | null;
  sla_warning_at: string | null;
  sla_critical_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  escalated: boolean;
  escalated_at: string | null;
  escalated_to: string | null;
  escalation_reason: string | null;
  safety_hold: boolean;
  safety_hold_reason: string | null;
  safety_hold_set_by: string | null;
  safety_hold_set_at: string | null;
  custom_fields: Record<string, unknown>;
  requestor_name: string | null;
  requestor_phone: string | null;
  requestor_email: string | null;
  created_by: string;
  tags: string[];
  attachments: TaskAttachment[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// Task attachment stored in JSONB
export interface TaskAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size: number;
  storage_key: string;
  uploaded_at: string;
  uploaded_by: string;
  url?: string;
}

// Task with related entities
export interface TaskWithDetails extends Task {
  task_type?: TaskType;
  assigned_to_user?: UserReference;
  assigned_by_user?: UserReference;
  created_by_user?: UserReference;
  escalated_to_user?: UserReference;
  // ESF joined data
  assigned_esf?: ESFReference;
  // Unified org tree joined display fields (incident_org_nodes)
  org_node_name?: string | null;
  org_node_identifier?: string | null;
  incident?: IncidentReference;
  // Parent task reference (if this task was spawned)
  parent_task?: TaskReference;
  // Child tasks count (tasks spawned from this task)
  child_task_count?: number;
  comment_count?: number;
  sla_status?: SlaStatus;
}

// Task history entry
export interface TaskHistory {
  id: string;
  task_id: string;
  from_status: TaskStatus | null;
  to_status: TaskStatus;
  changed_by: string;
  changed_at: string;
  /** P2-04 field time: effective time (device time bounded by the server's skew rules). */
  occurred_at?: string;
  /** The device clock as sent; null for console actions. */
  device_occurred_at?: string | null;
  received_at?: string;
  /** The device clock was outside the accepted bounds. */
  clock_suspect?: boolean;
  reason: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  changed_by_user?: UserReference;
}

// Task comment
export interface TaskComment {
  id: string;
  task_id: string;
  parent_id: string | null;
  author_id: string;
  content: string;
  comment_type: 'comment' | 'status_change' | 'escalation' | 'system';
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  author?: UserReference;
  replies?: TaskComment[];
}

// Task assignment record
export interface TaskAssignment {
  id: string;
  task_id: string;
  assigned_to: string;
  assigned_by: string;
  assigned_at: string;
  unassigned_at: string | null;
  unassigned_by: string | null;
  unassign_reason: string | null;
  is_escalation: boolean;
  escalation_level: number;
  assigned_to_user?: UserReference;
  assigned_by_user?: UserReference;
}

// Request DTOs
export interface CreateTaskData {
  /** Client-generated UUID (P2-05): the server's create is insert-or-return on it. */
  id?: string;
  incident_id?: string;
  task_type_id?: string;
  title: string;
  description?: string;
  priority?: TaskPriority;
  assigned_to?: string;
  // ESF assignment (can coexist with user assignment)
  assigned_esf_id?: string;
  // Unified org tree assignment (incident_org_nodes)
  org_node_id?: string | null;
  location_name?: string;
  location_address?: string;
  location_coordinates?: LocationCoordinates;
  due_at?: string;
  custom_fields?: Record<string, unknown>;
  requestor_name?: string;
  requestor_phone?: string;
  requestor_email?: string;
  tags?: string[];
  source_module?: string;
  source_id?: string;
  // Parent task ID for spawned tasks
  parent_task_id?: string;
  // Protocol activation ID for protocol-created tasks
  protocol_instance_id?: string;
}

export interface UpdateTaskData {
  title?: string;
  description?: string;
  priority?: TaskPriority;
  // ESF assignment (can coexist with user assignment)
  assigned_esf_id?: string | null;
  // Unified org tree assignment (incident_org_nodes)
  org_node_id?: string | null;
  location_name?: string;
  location_address?: string;
  location_coordinates?: LocationCoordinates | null;
  due_at?: string | null;
  custom_fields?: Record<string, unknown>;
  requestor_name?: string;
  requestor_phone?: string;
  requestor_email?: string;
  tags?: string[];
}

export interface UpdateTaskStatusData {
  status: TaskStatus;
  reason?: string;
  notes?: string;
}

export interface AssignTaskData {
  assigned_to: string;
  notes?: string;
}

export interface EscalateTaskData {
  escalate_to: string;
  reason: string;
}

export interface CreateCommentData {
  content: string;
  parent_id?: string;
}

// Filter options
export interface TaskFilters {
  status?: TaskStatus | TaskStatus[];
  priority?: TaskPriority | TaskPriority[];
  task_type_id?: string;
  incident_id?: string;
  assigned_to?: string;
  // ESF and unified org tree filters
  assigned_esf_id?: string;
  org_node_id?: string;
  created_by?: string;
  due_before?: string;
  due_after?: string;
  search?: string;
  include_deleted?: boolean;
  escalated?: boolean;
  unassigned?: boolean;
  my_tasks?: boolean;
  sla_status?: SlaStatus;
}

// Paginated response
export interface PaginatedTasks {
  data: TaskWithDetails[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Stats response
export interface TaskStats {
  total: number;
  open: number;
  assigned: number;
  in_progress: number;
  pending_review: number;
  complete: number;
  complete_today: number;
  my_tasks: number;
  overdue: number;
}

// Labels and colors for UI
export const STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  pending_review: 'Pending Review',
  complete: 'Complete',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
};

// Color budget: statuses map onto the severity scale (calm → elevated → warning)
// instead of one arbitrary hue per status. Semantic tokens work in both themes.
export const STATUS_COLORS: Record<TaskStatus, string> = {
  open: 'bg-surface-tertiary text-theme-secondary',
  assigned: 'bg-surface-tertiary text-theme-secondary',
  in_progress: 'bg-[color:var(--severity-elevated-bg)] text-[color:var(--severity-elevated-text)]',
  pending_review: 'bg-[color:var(--severity-warning-bg)] text-[color:var(--severity-warning-text)]',
  complete: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  on_hold: 'bg-surface-tertiary text-theme-secondary',
};

export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

// Aligned with PriorityFlag's severity mapping (low=calm, medium=elevated,
// high=warning, urgent=critical) so priority reads on one scale everywhere.
export const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'bg-surface-tertiary text-theme-secondary',
  medium: 'bg-[color:var(--severity-elevated-bg)] text-[color:var(--severity-elevated-text)]',
  high: 'bg-[color:var(--severity-warning-bg)] text-[color:var(--severity-warning-text)]',
  urgent: 'bg-[color:var(--severity-critical-bg)] text-[color:var(--severity-critical-text)]',
};

export const SLA_STATUS_COLORS: Record<SlaStatus, string> = {
  ok: 'text-green-600 dark:text-green-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  critical: 'text-orange-600 dark:text-orange-400',
  overdue: 'text-red-600 dark:text-red-400',
};

// Badge variants for SLA pills (shared Badge component). Color semantics
// mirror SLA_STATUS_COLORS; the raw color maps below are kept for non-pill
// consumers (banners, text tinting in kanban/card/compact views).
export const SLA_STATUS_VARIANTS: Record<SlaStatus, BadgeVariant> = {
  ok: 'success',
  warning: 'warning',
  critical: 'orange',
  overdue: 'danger',
};

export const SLA_STATUS_LABELS: Record<SlaStatus, string> = {
  ok: 'On Track',
  warning: 'SLA Warning',
  critical: 'SLA Critical',
  overdue: 'Overdue',
};

export const SLA_STATUS_BG_COLORS: Record<SlaStatus, string> = {
  ok: 'bg-green-100 dark:bg-green-900',
  warning: 'bg-yellow-100 dark:bg-yellow-900',
  critical: 'bg-orange-100 dark:bg-orange-900',
  overdue: 'bg-red-100 dark:bg-red-900',
};

/**
 * Valid status transitions for the tasks module.
 *
 * This MUST mirror the canonical map in backend/src/modules/tasks/task.types.ts
 * (VALID_STATUS_TRANSITIONS). If you change transitions here, update the backend
 * map as well -- the workflow engine derives its state graph from it.
 */
export const VALID_STATUS_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  open: ['assigned', 'in_progress', 'cancelled', 'on_hold'],
  assigned: ['open', 'in_progress', 'pending_review', 'complete', 'cancelled', 'on_hold'],
  in_progress: ['open', 'assigned', 'pending_review', 'complete', 'cancelled', 'on_hold'],
  pending_review: ['in_progress', 'complete', 'cancelled'],
  complete: ['open'],     // allow reopen
  cancelled: ['open'],    // allow reopen
  on_hold: ['open', 'assigned', 'in_progress'],
};

/** Simplified field-mode transitions for mobile workers */
export const FIELD_STATUS_TRANSITIONS: Record<TaskStatus, { label: string; target: TaskStatus; color: string; requiresConfirm?: boolean }[]> = {
  open: [
    { label: 'Start', target: 'in_progress', color: 'blue' },
    { label: 'Cancel', target: 'cancelled', color: 'red', requiresConfirm: true },
  ],
  assigned: [
    { label: 'Start', target: 'in_progress', color: 'blue' },
    { label: 'Cancel', target: 'cancelled', color: 'red', requiresConfirm: true },
  ],
  in_progress: [
    { label: 'Complete', target: 'complete', color: 'green', requiresConfirm: true },
    { label: 'On Hold', target: 'on_hold', color: 'yellow', requiresConfirm: true },
    { label: 'Cancel', target: 'cancelled', color: 'red', requiresConfirm: true },
  ],
  on_hold: [
    { label: 'Resume', target: 'in_progress', color: 'blue' },
    { label: 'Cancel', target: 'cancelled', color: 'red', requiresConfirm: true },
  ],
  pending_review: [
    { label: 'Complete', target: 'complete', color: 'green', requiresConfirm: true },
    { label: 'Cancel', target: 'cancelled', color: 'red', requiresConfirm: true },
  ],
  complete: [],
  cancelled: [],
};

// Helper functions
export function getSlaStatus(task: Task): SlaStatus {
  if (!task.due_at) return 'ok';

  const now = new Date();
  const dueAt = new Date(task.due_at);

  if (now >= dueAt) return 'overdue';
  if (task.sla_critical_at && now >= new Date(task.sla_critical_at)) return 'critical';
  if (task.sla_warning_at && now >= new Date(task.sla_warning_at)) return 'warning';
  return 'ok';
}

export function formatTimeRemaining(dueAt: string | null): string {
  if (!dueAt) return '';

  const now = new Date();
  const due = new Date(dueAt);
  const diff = due.getTime() - now.getTime();

  if (diff < 0) {
    const overdue = Math.abs(diff);
    const hours = Math.floor(overdue / (1000 * 60 * 60));
    const minutes = Math.floor((overdue % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h ${minutes}m overdue`;
    }
    return `${minutes}m overdue`;
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h remaining`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  }
  return `${minutes}m remaining`;
}

// Icon mapping for task types
export const TASK_TYPE_ICONS: Record<string, string> = {
  'user-check': 'UserCheck',
  'flame': 'Flame',
  'building': 'Building',
  'zap': 'Zap',
  'truck': 'Truck',
  'clipboard-list': 'ClipboardList',
};

/**
 * Generate a task title from a template by replacing placeholders with values from custom_fields.
 * Supports both single braces {field_name} and double braces {{field_name}} syntax.
 */
export function generateTitleFromTemplate(
  template: string,
  customFields: Record<string, unknown>
): string {
  const replacer = (match: string, fieldName: string) => {
    const value = customFields[fieldName];
    if (value === undefined || value === null || value === '') {
      return match; // Keep placeholder if no value
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  // First replace double braces {{field_name}} (supports dot notation e.g. {{parent_task.location_name}})
  let result = template.replace(/\{\{([\w.]+)\}\}/g, replacer);
  // Then replace single braces {field_name} for backwards compatibility
  result = result.replace(/\{([\w.]+)\}/g, replacer);

  return result;
}

/**
 * Check if a title template has all placeholders filled.
 * Checks for both single and double brace patterns.
 */
export function isTitleTemplateComplete(
  template: string,
  customFields: Record<string, unknown>
): boolean {
  const generated = generateTitleFromTemplate(template, customFields);
  // Check if any placeholder patterns remain (single or double braces)
  return !/\{\{?[\w.]+\}?\}/.test(generated);
}
